import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { filter, map, of, take, timeout } from 'rxjs';
import { FeatureFlagService } from '../services/feature-flag.service';

// Route data key carrying the Unleash flag name, e.g.
//   { path: 'assistant', canActivate: [featureFlagGuard],
//     data: { featureFlag: 'panorama_inexp_2498_assistant' } }
const FEATURE_FLAG_DATA_KEY = 'featureFlag';

// If Unleash never becomes ready (endpoint down), we stop waiting after this
// long and fail open — matching FeatureFlagService.createFlagSignal(), which
// treats "not ready" as enabled so a flag outage never hides shipped features.
const READY_TIMEOUT_MS = 3000;

/**
 * Route guard that gates a route behind an Unleash feature flag named in the
 * route's `data.featureFlag`. Unlike a naive `isEnabled()` check, it waits for
 * the flag client to be ready before deciding, so an enabled user hard-loading
 * the URL is not bounced during the brief init window. When the flag is off the
 * user is redirected home; with no flag configured the guard is a no-op.
 */
export const featureFlagGuard: CanActivateFn = (route) => {
  const flagName = route.data?.[FEATURE_FLAG_DATA_KEY] as string | undefined;
  if (!flagName) {
    return true;
  }

  const featureFlags = inject(FeatureFlagService);
  const router = inject(Router);

  return featureFlags.isReady().pipe(
    filter((ready) => ready),
    take(1),
    // Unleash down: never rendered ready → allow (fail open), see comment above.
    timeout({ first: READY_TIMEOUT_MS, with: () => of(true) }),
    map(() =>
      featureFlags.isEnabled(flagName) ? true : router.parseUrl('/'),
    ),
  );
};
